import { useQuery } from "@tanstack/react-query";
import { FileText, FileUp, TrendingUp, Users } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Cell, Pie, PieChart } from "recharts";
import { Loader } from "~/components/Loader";
import { Card, CardContent } from "~/components/ui/card";
import { ChartConfig, ChartContainer } from "~/components/ui/chart";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { UserAvatar } from "~/components/ui/user-avatar";
import { customInstance } from "~/custom-instance";
import { getStoredLocale, useOrganizationParams } from "~/helpers";

type TimePeriod = "week" | "month" | "quarter" | "all";

interface TeamMemberProductivity {
  userId: string;
  name: string;
  email: string;
  uploadsCount: number;
  exportsCount: number;
  casesCreated: number;
  lastActiveAt: string | null;
}

interface ProductivityData {
  period: TimePeriod;
  totalUploads: number;
  totalExports: number;
  activeContributors: number;
  totalPagesProcessed: number;
  teamMembers: TeamMemberProductivity[];
}

const CHART_COLORS = [
  "#3b82f6",
  "#22c55e",
  "#f472b6",
  "#f97316",
  "#06b6d4",
  "#8b5cf6",
  "#6b7280",
];

const getProductivityData = (
  organizationId: string,
  period: TimePeriod,
  signal?: AbortSignal
) => {
  return customInstance<ProductivityData>({
    url: `${import.meta.env.VITE_BASE_API_URL}/organizations/${organizationId}/analytics/productivity`,
    method: "get",
    params: { period },
    signal,
  });
};

const useGetProductivityData = (organizationId: string, period: TimePeriod) => {
  return useQuery({
    queryKey: ["analytics", "productivity", organizationId, period],
    queryFn: ({ signal }) =>
      getProductivityData(organizationId, period, signal),
    enabled: !!organizationId,
  });
};

const chartConfig = {
  uploads: {
    label: "Uploads",
  },
} satisfies ChartConfig;

export const ProductivitySection: FC = () => {
  const { organizationId } = useOrganizationParams();
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>("all");
  const storedLocale = getStoredLocale() || "en";
  const intl = useIntl();

  const { data, isLoading, isError } = useGetProductivityData(
    organizationId,
    selectedPeriod
  );

  const THRESHOLD_FOR_OTHER = 10;

  const chartData = (() => {
    const members = data?.teamMembers.filter((m) => m.uploadsCount > 0) ?? [];
    const aboveThreshold = members.filter(
      (m) => m.uploadsCount >= THRESHOLD_FOR_OTHER
    );
    const belowThreshold = members.filter(
      (m) => m.uploadsCount < THRESHOLD_FOR_OTHER
    );

    const result = aboveThreshold.map((member, index) => ({
      name: member.name || member.email,
      value: member.uploadsCount,
      fill: CHART_COLORS[index % CHART_COLORS.length],
    }));

    if (belowThreshold.length > 0) {
      const otherTotal = belowThreshold.reduce(
        (sum, m) => sum + m.uploadsCount,
        0
      );
      result.push({
        name: intl.formatMessage({
          id: "settings.analytics.productivity.other",
          defaultMessage: "Other",
        }),
        value: otherTotal,
        fill: CHART_COLORS[result.length % CHART_COLORS.length],
      });
    }

    return result;
  })();

  const totalUploads = chartData.reduce((sum, item) => sum + item.value, 0);

  const maxUploads = Math.max(
    1,
    ...(data?.teamMembers.map((m) => m.uploadsCount) ?? [])
  );
  const maxCases = Math.max(
    1,
    ...(data?.teamMembers.map((m) => m.casesCreated) ?? [])
  );

  const getPeriodLabel = (period: TimePeriod) => {
    switch (period) {
      case "week":
        return "Week";
      case "month":
        return "Month";
      case "quarter":
        return "Quarter";
      case "all":
        return "All Time";
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[200px]">
        <Loader />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col gap-6">
        <span className="text-sm text-muted-foreground">
          <FormattedMessage
            id="settings.productivity.error"
            defaultMessage="Unable to load productivity data. Please try again later."
          />
        </span>
      </div>
    );
  }

  const hasNoActivity =
    data.totalUploads === 0 &&
    data.activeContributors === 0 &&
    data.totalPagesProcessed === 0;

  if (hasNoActivity && selectedPeriod === "all") {
    return (
      <Card className="w-full max-w-[924px]">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <TrendingUp className="w-12 h-12 text-muted-foreground mb-4" />
            <h4 className="text-lg font-medium mb-2">
              <FormattedMessage
                id="settings.productivity.empty.title"
                defaultMessage="No activity yet"
              />
            </h4>
            <p className="text-sm text-muted-foreground max-w-md">
              <FormattedMessage
                id="settings.productivity.empty.description"
                defaultMessage="Upload documents to your cases to start tracking team productivity metrics. Your upload stats, contribution charts, and team activity will appear here."
              />
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-[924px]">
      <Tabs
        value={selectedPeriod}
        onValueChange={(value) => setSelectedPeriod(value as TimePeriod)}
      >
        <TabsList>
          <TabsTrigger value="week">
            <FormattedMessage
              id="settings.analytics.productivity.week"
              defaultMessage="Week"
            />
          </TabsTrigger>
          <TabsTrigger value="month">
            <FormattedMessage
              id="settings.analytics.productivity.month"
              defaultMessage="Month"
            />
          </TabsTrigger>
          <TabsTrigger value="quarter">
            <FormattedMessage
              id="settings.analytics.productivity.quarter"
              defaultMessage="Quarter"
            />
          </TabsTrigger>
          <TabsTrigger value="all">
            <FormattedMessage
              id="settings.analytics.productivity.allTime"
              defaultMessage="All Time"
            />
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            <span className="text-sm text-muted-foreground">
              <FormattedMessage
                id="settings.analytics.productivity.uploadsBy"
                defaultMessage="Documents Uploaded by Team ({period})"
                values={{ period: getPeriodLabel(selectedPeriod) }}
              />
            </span>
            <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-8 w-full">
              <ChartContainer
                config={chartConfig}
                className="h-[200px] w-[200px] shrink-0"
              >
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    strokeWidth={0}
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <text
                    x="50%"
                    y="45%"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="fill-foreground text-3xl font-bold"
                  >
                    {totalUploads.toLocaleString(storedLocale)}
                  </text>
                  <text
                    x="50%"
                    y="60%"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="fill-muted-foreground text-xs"
                  >
                    {intl.formatMessage({
                      id: "settings.analytics.productivity.totalUploaded",
                      defaultMessage: "Total uploaded",
                    })}
                  </text>
                </PieChart>
              </ChartContainer>
              <div className="flex flex-col gap-2 flex-1 w-full lg:w-auto">
                {chartData.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: item.fill }}
                    />
                    <span className="text-sm">
                      {item.name}{" "}
                      <span className="font-semibold">{item.value}</span>
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row lg:flex-col gap-4 w-full lg:w-auto">
                <Card className="w-full sm:w-auto lg:w-[200px]">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex flex-col gap-1.5">
                      <div className="flex justify-between">
                        <span className="text-xs text-muted-foreground font-semibold">
                          <FormattedMessage
                            id="settings.analytics.productivity.totalUploads"
                            defaultMessage="Total Uploads"
                          />
                        </span>
                        <FileUp className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <span className="text-card-foreground text-2xl font-semibold tracking-tight">
                        {data.totalUploads.toLocaleString(storedLocale)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
                <Card className="w-full sm:w-auto lg:w-[200px]">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex flex-col gap-1.5">
                      <div className="flex justify-between">
                        <span className="text-xs text-muted-foreground font-semibold">
                          <FormattedMessage
                            id="settings.analytics.productivity.contributors"
                            defaultMessage="Active Contributors"
                          />
                        </span>
                        <Users className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <span className="text-card-foreground text-2xl font-semibold tracking-tight">
                        {data.activeContributors.toLocaleString(storedLocale)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
                <Card className="w-full sm:w-auto lg:w-[200px]">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex flex-col gap-1.5">
                      <div className="flex justify-between">
                        <span className="text-xs text-muted-foreground font-semibold">
                          <FormattedMessage
                            id="settings.analytics.productivity.pagesProcessed"
                            defaultMessage="Pages Processed"
                          />
                        </span>
                        <FileText className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <span className="text-card-foreground text-2xl font-semibold tracking-tight">
                        {(data.totalPagesProcessed ?? 0).toLocaleString(
                          storedLocale
                        )}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Table className="w-full">
        <TableHeader>
          <TableRow className="!text-zinc-500 !text-sm !font-medium">
            <TableHead className="px-4">
              <FormattedMessage
                id="settings.analytics.productivity.teamMember"
                defaultMessage="Team member"
              />
            </TableHead>
            <TableHead className="px-4 w-24 sm:w-48">
              <FormattedMessage
                id="settings.analytics.productivity.casesCreated"
                defaultMessage="Cases Created"
              />
            </TableHead>
            <TableHead className="px-4 w-24 sm:w-48">
              <FormattedMessage
                id="settings.analytics.productivity.uploads"
                defaultMessage="Uploads"
              />
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.teamMembers
            .sort((a, b) => b.uploadsCount - a.uploadsCount)
            .map((member) => (
              <TableRow key={member.userId}>
                <TableCell className="p-4">
                  <div className="flex items-center gap-2">
                    <UserAvatar name={member.name || member.email} size="sm" />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">
                        {member.name || member.email}
                      </span>
                      {member.name && (
                        <span className="text-xs text-muted-foreground">
                          {member.email}
                        </span>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="p-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium w-8">
                      {member.casesCreated}
                    </span>
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-500 rounded-full"
                        style={{
                          width: `${(member.casesCreated / maxCases) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </TableCell>
                <TableCell className="p-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium w-8">
                      {member.uploadsCount}
                    </span>
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full"
                        style={{
                          width: `${(member.uploadsCount / maxUploads) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </div>
  );
};
